import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import TableMain from './TableMain';
// import Api from "../../services/CreatePhase/CreatePhaseService";
// import CorgeeStorageHelper from "../../services/CorgeeStorageHelper";
import './CreatePhase.css';
import CorgeeModal from '@/components/Globals/CorgeeModal';
import RenderInBody from '@/components/utils/RenderInBody';
import ClickOutSide from '@/components/Globals/ClickOutSide';
import conversationApi from '@/services/conversationApi';
import { getPersonData } from '@/components/utils/CorgeeHelper';
import { Modal } from '@/components/utils/Modal';
import { useToast } from '@components/ui/Toast';
// ─── Types ─────────────────────────────────────────────────────────────────────

interface PhaseItem {
  content: string;
  edit: boolean;
}

interface PhaseOption {
  label: string;
  value: number;
}

interface ValidatePhase {
  ConversationId: string;
  Title: string;
  Icon: string;
  YesPhase: string;
  NoPhase: string;
  Order: number;
}

interface ConversationParam {
  ConversationId: string;
  CoreTaskId: string;
  ProjectId: string;
  ProjectName: string;
  HasPhases: boolean;
  IsAdmin: boolean;
  ConversationLayers: unknown[];
  ConfigValueIdPhase?: number;
}

interface CustomFieldLayer {
  LayerId: number;
  CustomFields: Array<{
    CustomFieldId: number;
    Value: string;
    FieldTypeId: number;
    Name: string;
  }>;
}

type SaveOutcome = 'updated' | 'skipped';

const PHASES_ERROR_MESSAGE = 'Phases could not be created. Please try again.';

const pluralTasks = (count: number) => `${count} task${count !== 1 ? 's' : ''}`;

const splitNoPhase = (noPhase: string) => (noPhase ? noPhase.split(', ').filter(Boolean) : []);

export interface CreatePhaseProps {
  config: Record<string, unknown> & { apiUrl?: string; token?: string };
  parameters: {
    ConversationIds: ConversationParam[];
    SocketConnectionId: string;
  };
  modal: Record<string, unknown>;
  onclosemodal?: () => void;
  loadingbarstart?: () => void;
  loadingbarcomplete?: () => void;
  onSuccess?: (conversationIds: string[]) => void;
}

// ─── Sub-components ────────────────────────────────────────────────────────────

const ValidationList: React.FC<{ validatePhases: ValidatePhase[] }> = ({ validatePhases }) => (
  <div className="multiple-phases-container">
    {validatePhases.map((phase, i) => (
      <div key={i}>
        <div>
          <div className="multiple-phases-icon">
            <div className={phase.Icon} />
          </div>
          <div className="multiple-phases-title">
            <div className="multiple-phases-description">{phase.Title}</div>
          </div>
        </div>
        <div className="multiple-detail-values">
          <div className="multiple-detail-value-item">
            <div className="multiple-detail-line-text">Phases created:</div>
            <div className="multiple-detail-line-value">{phase.YesPhase}</div>
          </div>
          <div className="multiple-detail-value-item">
            <div className="multiple-detail-line-text">Phases will be created:</div>
            <div className="multiple-detail-line-value">{phase.NoPhase}</div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const AutocompleteDropdown: React.FC<{
  options: string[];
  onSelect: (value: string) => void;
  onClickOutside: () => void;
}> = ({ options, onSelect, onClickOutside }) =>
{
  const clickOutSideWhiteList = [
    { type: 'class', value: 'react-autosuggest__suggestion' },
    { type: 'class', value: 'autosuggest__suggestion' },
    { type: 'class', value: 'autosuggest__suggestions-list' },
    { type: 'class', value: 'react-autosuggest__suggestions-container' },
    { type: 'class', value: 'react-autosuggest__suggestions-container--open' },
  ];
  let actionMenuElement = document.getElementById('input-add-phases'),
    left = 0,
    top = 0;
  left = actionMenuElement?.getBoundingClientRect().left ?? 0;
  top =
    (actionMenuElement?.offsetHeight ?? 0) + (actionMenuElement?.getBoundingClientRect().top ?? 0);
  return (
    <div id="react-autowhatever-0">
      <RenderInBody>
        <ClickOutSide onClickOutSide={onClickOutside} whiteList={clickOutSideWhiteList}>
          <div
            id="react-autowhatever-1"
            style={{ left: left, top: top, zIndex: 2000 }}
            className="react-autosuggest__suggestions-container react-autosuggest__suggestions-container--open"
          >
            <ul role="listbox" className="react-autosuggest__suggestions-list">
              {options.map((option, index) => (
                <li
                  key={index}
                  role="option"
                  id={`react-autowhatever-${index}`}
                  className="react-autosuggest__suggestion"
                  onClick={() => onSelect(option)}
                >
                  <span>{option}</span>
                </li>
              ))}
            </ul>
          </div>
        </ClickOutSide>
      </RenderInBody>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

const CreatePhase: React.FC<CreatePhaseProps> = props =>
{
  const { config, parameters, onclosemodal, loadingbarstart, loadingbarcomplete, onSuccess } =
    props;
  const configApiUrl = (config.apiUrl ?? '') as string;
  const configToken = (config.token ?? '') as string;
  const toast = useToast();

  const _requestConfig = { api: config };
  void _requestConfig;

  // ── State ──────────────────────────────────────────────────────────────────
  const [items, setItems] = useState<PhaseItem[]>([]);
  const [message, setMessage] = useState({ show: false, text: '' });
  const [editValue, setEditValue] = useState('');
  const [editIndex, setEditIndex] = useState(0);
  const [options, setOptions] = useState<string[]>([]);
  const [optionsPhases, setOptionsPhases] = useState<PhaseOption[]>([]);
  const [filterOptions, setFilterOptions] = useState<string[]>([]);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [browseModalOpen, setBrowseModalOpen] = useState(false);
  const [contentFile, setContentFile] = useState<string[]>([]);
  const [showValidation, setShowValidation] = useState(false);
  const [validatePhases, setValidatePhases] = useState<ValidatePhase[]>([]);
  const [isValidated, setIsValidated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  useEffect(() =>
  {
    const handleResize = () => setAutocompleteOpen(false);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() =>
  {
    conversationApi
      .GetAllPhases(configApiUrl, configToken)
      .then((response: Array<{ Title: string; PhaseId: number }>) =>
      {
        setOptions(response.map(r => r.Title));
        setOptionsPhases(response.map(r => ({ label: r.Title, value: r.PhaseId })));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const showMessage = (text: string) => setMessage({ show: true, text });
  const clearMessage = () => setMessage({ show: false, text: '' });
  const isDuplicate = (list: PhaseItem[], value: string) =>
    list.some(i => i.content.toLowerCase() === value.toLowerCase());

  // ── Add / Delete / Edit ────────────────────────────────────────────────────
  const addPhase = () =>
  {
    const value = inputRef.current?.value?.trim() ?? '';

    if (!value) return showMessage('This field cannot be blank.');
    if (isDuplicate(items, value)) return showMessage('Item already added.');

    setItems(prev => [...prev, { content: value, edit: false }]);
    clearMessage();
    setAutocompleteOpen(false);
    setFilterOptions([]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const deletePhase = (index: number) => setItems(prev => prev.filter((_, i) => i !== index));

  const startEditPhase = (index: number) =>
  {
    if (items.some(i => i.edit)) return;
    setItems(prev => prev.map((item, i) => ({ ...item, edit: i === index })));
    setEditValue(items[index]?.content ?? '');
    setEditIndex(index);
  };

  const saveEditPhase = (index: number) =>
  {
    if (!editValue.trim()) return;
    setItems(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, content: editValue.trim(), edit: false } : item,
      ),
    );
  };

  const cancelEditPhase = () =>
  {
    setItems(prev => prev.map(item => ({ ...item, edit: false })));
    setEditValue('');
    setEditIndex(0);
  };

  // ── Keyboard handlers ──────────────────────────────────────────────────────
  const onKeyDownAdd = (e: React.KeyboardEvent<HTMLInputElement>) =>
  {
    if (message.show) clearMessage();
    if (e.key === 'Enter')
    {
      // Modal wraps its content in a <form>; stop Enter from submitting it
      e.preventDefault();
      addPhase();
      return;
    }

    const value = inputRef.current?.value ?? '';
    if (value.length >= 2)
    {
      setFilterOptions(options.filter(o => o.includes(value)));
      setAutocompleteOpen(true);
    }
    else
    {
      setAutocompleteOpen(false);
      setFilterOptions([]);
    }
  };

  const onKeyDownEdit = (e: React.KeyboardEvent<HTMLInputElement>) =>
  {
    if (e.key === 'Enter')
    {
      e.preventDefault();
      saveEditPhase(editIndex);
    }
    else if (e.key === 'Escape') cancelEditPhase();
  };

  // ── Autocomplete ───────────────────────────────────────────────────────────
  const selectSuggestion = (value: string) =>
  {
    if (inputRef.current) inputRef.current.value = value;
    addPhase();
    setAutocompleteOpen(false);
  };

  // ── File handling ──────────────────────────────────────────────────────────
  const mergePhasesFromFile = (fileItems: string[], replaceAll: boolean) =>
  {
    const base = replaceAll ? [] : items;
    const next = [...base];
    for (const str of fileItems)
    {
      const content = str.trim();
      if (content && !isDuplicate(next, content)) next.push({ content, edit: false });
    }
    setItems(next);
    setBrowseModalOpen(false);
    clearMessage();
  };

  const handleFileLoaded = (raw: string) =>
  {
    try
    {
      const parsed = raw
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      if (items.length === 0)
      {
        mergePhasesFromFile(parsed, true);
      }
      else
      {
        setContentFile(parsed);
        setBrowseModalOpen(true);
        clearMessage();
      }
    }
    catch
    {
      alert('File Error');
    }
  };

  const handleFileChange = (file: File) =>
  {
    const reader = new FileReader();
    reader.onloadend = e => handleFileLoaded(e.target?.result as string);
    reader.readAsText(file);
  };

  // ── Validate & Save ────────────────────────────────────────────────────────
  const backToEdit = () =>
  {
    setShowValidation(false);
    setIsValidated(false);
    setValidatePhases([]);
  };

  // Copies the task's Estimated/Remaining values into a "Default" phase before its first real phase
  // A phase inherits the task's layers and must never be created without one.
  // Selected rows can carry ConversationLayers as null/empty, so fall back to the API.
  const resolvePhaseLayers = async (conv: ConversationParam): Promise<unknown[]> =>
  {
    const validLayers = (layers?: unknown[] | null) =>
      (layers || []).filter(x => (x as { LayerId?: number }).LayerId !== 0);

    const currentLayers = validLayers(conv.ConversationLayers);
    if (currentLayers.length > 0) return currentLayers;

    const fetchedConv = await conversationApi.getConversationById(
      configApiUrl,
      configToken,
      Number(conv.ConversationId),
      { isConversationDetailsView: false },
    );
    const fetchedLayers = validLayers(fetchedConv?.ConversationLayers as unknown[] | undefined);
    if (fetchedLayers.length === 0)
    {
      throw new Error(`Task ${conv.ConversationId} has no layers; phases were not created`);
    }
    return fetchedLayers;
  };

  const createDefaultPhase = async (
    conv: ConversationParam,
    phaseLayers: unknown[],
    getEmptyFields: (configValueId: number) => Promise<CustomFieldLayer[]>,
  ): Promise<boolean> =>
  {
    const layers: CustomFieldLayer[] = await conversationApi.GetCustomFieldsByItemId(
      configApiUrl,
      configToken,
      Number(conv.CoreTaskId),
      false,
      Number(conv.ProjectId),
    );

    const copyCustoms: Array<{
      CustomFieldId: number;
      Value: string;
      CoreTaskID: number;
      FieldTypeId: number;
      LayerId: number;
      Name: string;
    }> = [];
    let saveDefault = false;

    for (const layer of layers)
    {
      for (const cf of layer.CustomFields)
      {
        if (cf.Value != null && cf.Value !== 'Not Set')
        {
          if (['Estimated', 'Remaining'].includes(cf.Name)) saveDefault = true;
          copyCustoms.push({ ...cf, CoreTaskID: 0, LayerId: layer.LayerId });
        }
      }
    }

    if (!saveDefault) return false;

    const configValueId = conv.ConfigValueIdPhase || getPersonData()?.ConfigValueIdPhases;
    const defaultLayers = await getEmptyFields(configValueId);

    const finalFields = copyCustoms.flatMap(cc =>
    {
      const layer = defaultLayers.find(d => d.LayerId === cc.LayerId);
      const match = layer?.CustomFields.find(f => f.Name === cc.Name);
      return match
        ? [
          {
            CustomFieldId: match.CustomFieldId,
            Value: cc.Value,
            CoreTaskID: 0,
            FieldTypeId: cc.FieldTypeId,
            CreatedByID: 1,
          },
        ]
        : [];
    });

    const defaultPhase = {
      Attachments: [],
      Body: '<span></span>',
      ConfigValueId: configValueId,
      ConversationGroups: [],
      ConversationId: conv.ConversationId,
      ConversationLayers: phaseLayers,
      CustomFields: finalFields,
      Icon: '',
      IsAdmin: conv.IsAdmin,
      IsFavorite: false,
      IsFromAssignmentReport: false,
      IsRedFlag: false,
      ProjectId: conv.ProjectId,
      ProjectName: conv.ProjectName,
      ShowDetails: true,
      SideMenuText: '',
      SocketConnectionId: parameters.SocketConnectionId,
      Tags: [],
      Title: 'Default',
      UserExternal: false,
    };

    const res = await conversationApi.ConversationNewPhase(configApiUrl, configToken, defaultPhase);
    await conversationApi.CreateNewPhase(configApiUrl, configToken, {
      ConversationId: conv.ConversationId,
      ConversationPhaseId: res.Conversation.ConversationId,
      Order: 0,
      Title: res.Conversation.Title,
      SocketConnectionId: parameters.SocketConnectionId,
      PhaseId: -1,
    });
    return true;
  };

  const processConversation = async (
    validate: ValidatePhase,
    phaseIdByTitle: Map<string, number>,
    getEmptyFields: (configValueId: number) => Promise<CustomFieldLayer[]>,
  ): Promise<SaveOutcome> =>
  {
    const conv = parameters.ConversationIds.find(
      c => String(c.ConversationId) === String(validate.ConversationId),
    );
    if (!conv) return 'skipped';

    const phaseLayers = await resolvePhaseLayers(conv);

    let modified = false;
    if (!conv.HasPhases) modified = await createDefaultPhase(conv, phaseLayers, getEmptyFields);

    // Phases of the same task stay sequential: each one is inserted at an explicit Order
    const titles = splitNoPhase(validate.NoPhase);
    for (const [x, title] of titles.entries())
    {
      const order = validate.Order + x + 1;
      const phaseId = phaseIdByTitle.get(title);

      const newPhase = {
        Attachments: [],
        Body: '<span></span>',
        ConfigValueId: getPersonData()?.ConfigValueIdPhases,
        ConversationGroups: [],
        ConversationId: conv.ConversationId,
        ConversationLayers: phaseLayers,
        CustomFields: [],
        Icon: '',
        IsAdmin: conv.IsAdmin,
        IsFavorite: false,
        IsFromAssignmentReport: false,
        IsRedFlag: false,
        ProjectId: conv.ProjectId,
        ProjectName: conv.ProjectName,
        ShowDetails: true,
        SideMenuText: '',
        SocketConnectionId: parameters.SocketConnectionId,
        Tags: [],
        Title: title,
        UserExternal: false,
        Order: order,
      };

      const cardResponse = await conversationApi.ConversationNewPhase(
        configApiUrl,
        configToken,
        newPhase,
      );
      if (!cardResponse?.Conversation)
      {
        throw new Error(`Phase "${title}" was not created for task ${conv.ConversationId}`);
      }

      await conversationApi.CreateNewPhase(configApiUrl, configToken, {
        ConversationId: conv.ConversationId,
        ConversationPhaseId: cardResponse.Conversation.ConversationId,
        Title: cardResponse.Conversation.Title,
        Order: order,
        PhaseId: phaseId,
        SocketConnectionId: parameters.SocketConnectionId,
      });
      modified = true;
    }

    return modified ? 'updated' : 'skipped';
  };

  const notifySaveResult = (updated: number, skipped: number, failed: number) =>
  {
    const total = updated + skipped + failed;
    if (updated === total)
    {
      toast.success(`${pluralTasks(updated)} updated`);
      return;
    }

    const parts = [`${updated} of ${pluralTasks(total)} updated`];
    if (skipped > 0) parts.push(`${pluralTasks(skipped)} already had these phases`);
    if (failed > 0) parts.push(`${pluralTasks(failed)} could not be modified`);
    const text = `${parts.join('. ')}.`;

    if (failed > 0) toast.warning(text);
    else if (updated === 0) toast.info(text);
    else toast.success(text);
  };

  const savePhases = async () =>
  {
    setIsSaving(true);
    clearMessage();
    loadingbarstart?.();

    try
    {
      // Phase options are shared by every task, so create the missing ones once and up front
      const phaseIdByTitle = new Map(optionsPhases.map(o => [o.label, o.value] as const));
      let lastPhaseId = Math.max(...optionsPhases.map(o => o.value), 0);
      const newOptions: PhaseOption[] = [];
      for (const title of validatePhases.flatMap(v => splitNoPhase(v.NoPhase)))
      {
        if (phaseIdByTitle.has(title)) continue;
        lastPhaseId += 1;
        phaseIdByTitle.set(title, lastPhaseId);
        newOptions.push({ label: title, value: lastPhaseId });
      }

      await Promise.all(
        newOptions.map(o =>
          conversationApi.CreateNewOptionPhase(configApiUrl, configToken, {
            PhaseId: o.value,
            Title: o.label,
          }),
        ),
      );
      if (newOptions.length > 0) setOptionsPhases(prev => [...prev, ...newOptions]);

      // Tasks sharing a config value reuse the same empty custom-field template request
      const emptyFieldsCache = new Map<number, Promise<CustomFieldLayer[]>>();
      const getEmptyFields = (configValueId: number) =>
      {
        let cached = emptyFieldsCache.get(configValueId);
        if (!cached)
        {
          cached = conversationApi.GetEmptyCustomFieldsByConfigValueId(
            configApiUrl,
            configToken,
            configValueId,
          ) as Promise<CustomFieldLayer[]>;
          emptyFieldsCache.set(configValueId, cached);
        }
        return cached;
      };

      // Every task runs at the same time; one failing task doesn't stop the others
      const results = await Promise.allSettled(
        validatePhases.map(v =>
          processConversation(v, phaseIdByTitle, getEmptyFields).then(outcome => ({
            conversationId: v.ConversationId,
            outcome,
          })),
        ),
      );

      const updatedIds: string[] = [];
      let skipped = 0;
      let failed = 0;
      for (const result of results)
      {
        if (result.status === 'rejected')
        {
          failed++;
          console.error('Failed to create phases for task:', result.reason);
        }
        else if (result.value.outcome === 'updated') updatedIds.push(result.value.conversationId);
        else skipped++;
      }

      if (updatedIds.length === 0 && failed > 0)
      {
        toast.error(PHASES_ERROR_MESSAGE);
        showMessage(PHASES_ERROR_MESSAGE);
        return;
      }

      notifySaveResult(updatedIds.length, skipped, failed);

      if (updatedIds.length > 0)
      {
        onSuccess?.(updatedIds);
        window.dispatchEvent(
          new CustomEvent('corgee:phases-created', {
            detail: {
              conversationIds: updatedIds,
            },
          }),
        );
      }

      onclosemodal?.();
    }
    catch (error)
    {
      console.error('Failed to create phases:', error);
      toast.error(PHASES_ERROR_MESSAGE);
      showMessage(PHASES_ERROR_MESSAGE);
    }
    finally
    {
      setIsSaving(false);
      loadingbarcomplete?.();
    }
  };

  const createPhases = async () =>
  {
    if (isSaving || isValidating) return;

    if (items.length === 0)
    {
      showMessage('There are no phases to be added.');
      return;
    }

    if (isValidated)
    {
      await savePhases();
      return;
    }

    // Input messages ("This field cannot be blank.", "Item already added.") belong to the edit step
    clearMessage();
    setIsValidating(true);
    loadingbarstart?.();
    try
    {
      const result: ValidatePhase[] = await conversationApi.GetValidationPhase(
        configApiUrl,
        configToken,
        parameters.ConversationIds.map(c => c.ConversationId).join(', '),
        items.map(i => i.content).join(', '),
      );
      setValidatePhases(result);
      setIsValidated(true);
      setShowValidation(true);
    }
    catch (error)
    {
      console.error('Failed to validate phases:', error);
      showMessage('Phases could not be validated. Please try again.');
    }
    finally
    {
      setIsValidating(false);
      loadingbarcomplete?.();
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Modal
      open={props.modal.open as boolean}
      onClose={isSaving ? undefined : props.onclosemodal}
      title="Add Phases"
      preventCloseClickOut
    >
      {/* Blocks the dashboard while saving so the card that owns this modal can't be scrolled away or reloaded */}
      {isSaving && createPortal(<div className="fixed inset-0 z-[99] cursor-wait" />, document.body)}

      <div className="create-phase-windows">
        {/* Hidden triggers kept for legacy AngularJS callers */}
        <a id="clickHiddenCreatePhases" className="button-hidden-save" onClick={createPhases} />
        <a id="clickHiddenBackPhases" className="button-hidden-save" onClick={backToEdit} />

        <div className="create-phase-body" onScroll={() => setAutocompleteOpen(false)}>
          {!showValidation && (
            <div>
              {/* CSV upload */}
              <div className="corgee-layout-table">
                <label
                  className="corgee-layout-table-cell corgee-label text-sm font-medium"
                  style={{ width: 120 }}
                >
                  Select a csv file:
                </label>
                <div className="corgee-layout-table-cell">
                  <span className="btn btn-default btn-file">
                    Browse
                    <input
                      ref={fileInputRef}
                      id="inputUploadFile"
                      className="corge-button"
                      type="file"
                      accept=".csv"
                      onChange={e =>
                      {
                        const file = e.target.files?.[0];
                        if (file)
                        {
                          handleFileChange(file);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }
                      }}
                    />
                  </span>
                </div>
              </div>

              {/* Phase list */}
              <div className="create-phase-droppable">
                <TableMain
                  items={items}
                  saveNewSet={setItems}
                  editPhases={startEditPhase}
                  onKeyDownEdit={onKeyDownEdit}
                  handleChangeEdit={e => setEditValue(e.target.value)}
                  selectValue={editValue}
                  saveEditPhase={saveEditPhase}
                  clickDelete={deletePhase}
                />
              </div>

              {/* Add input */}
              <div className="corgee-layout-table">
                <div className="corgee-layout-table-cell">
                  <input
                    ref={inputRef}
                    id="input-add-phases"
                    style={{ width: 300 }}
                    className="corgee-input corgee-input-with-btn"
                    type="text"
                    aria-invalid="false"
                    onKeyDown={onKeyDownAdd}
                  />
                  <a className="corgee-button corgee-add" onClick={addPhase}>
                    Add
                  </a>
                </div>
              </div>
            </div>
          )}

          {showValidation && <ValidationList validatePhases={validatePhases} />}
        </div>

        {autocompleteOpen && filterOptions.length > 0 && (
          <AutocompleteDropdown
            options={filterOptions}
            onSelect={selectSuggestion}
            onClickOutside={() => setAutocompleteOpen(false)}
          />
        )}

        {message.show && <label className="messageEmptyAddPhases">{message.text}</label>}

        <div className="pt-3 mr-2 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
          <button
            type="button"
            disabled={isValidating || isSaving}
            onClick={() =>
            {
              createPhases();
            }}
            className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isValidating ? 'Validating...' : isSaving ? 'Saving...' : 'Create'}
          </button>
          <button
            type="button"
            disabled={isSaving}
            onClick={props.onclosemodal}
            className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>

        {browseModalOpen && (
          <CorgeeModal
            className="corgeeModalInputBrowse"
            openModal={browseModalOpen}
            onSave={() => mergePhasesFromFile(contentFile, false)}
            title="Merge Items"
            onClose={() => setBrowseModalOpen(false)}
            isDraggable
            closeOnEscape
            hasMaxHeight
            allowSaveAndNewCheckBox={false}
            hideActionButtons={false}
            width={400}
            height={120}
            textButton="Yes"
            saveButtonColor="#508ca6"
            cancelButtonName="No"
            cancelButtonClick={() => mergePhasesFromFile(contentFile, true)}
          >
            <div className="label-phase-modal">
              Do you want to merge CSV values with current values?
            </div>
          </CorgeeModal>
        )}
      </div>
    </Modal>
  );
};

export default CreatePhase;
